
import { Component, Prop, Vue } from 'bits/vue'

/**
 * @displayName Base Button
 */
import { navigateTo } from 'bits/location'

@Component({})
export default class BaseButton extends Vue {
  /**
   * Determines if button is disabled
   * @default false
   */
  @Prop({ default: false }) disabled!: boolean
  /**
   * Determines the button's main style theme
   * @values contained, flat, text, fat-contained, fat-text
   * @default text
   */
  @Prop() variation!: string
  /**
   * Determines the color of button
   * @values default, black, white, primary
   */
  @Prop({ default: 'default' }) colorScheme!: 'default' | 'black' | 'white' | 'green' | 'orange' | 'grey'
  /**
   * Allows clicking the button navigate to a URL
   */
  @Prop() href!: string
  /**
   * Determines where the url should be opened in
   * @values _self, _blank
   * @default _self
   */
  @Prop({ default: '_self' }) hrefTarget!: string
  get hasIcon(): boolean {
    return !!this.$slots.icon
  }
  /**
   * Determines if text-label should have margin
   * @default true
   */
  @Prop({ default: true }) xTextMargin!: boolean

  get hasText(): boolean {
    return !!this.$slots.default && this.$slots.default.toString().trim() !== ''
  }
  get variationClass(): string {
    switch (this.variation) {
      case 'contained':
      case 'flat':
        return 'flat-button'
      case 'text':
        return 'text-button'
      case 'fat-contained':
        return 'fat-contained-button'
      case 'small-contained':
        return 'small-contained-button'
      case 'large-contained':
        return 'large-contained-button'
      case 'fat-text':
        return 'fat-text-button'
      case 'fat-text-lower':
        return 'fat-text-button lower'
      case 'bordered-text':
        return 'fat-text-button bordered-text-button'
      case 'flat-bordered':
        return 'flat-button bordered-flat-button'
      default:
        return 'text-button'
    }
  }
  get iconClass(): string {
    const iconClasses: string[] = []
    if (this.hasIcon) {
      iconClasses.push('icon-button')
    }
    if (!this.hasText) {
      iconClasses.push('button-without-text')
    }
    if (this.variation === 'bordered-icon') {
      iconClasses.push('bordered-icon-button')
    }
    return iconClasses.join(' ')
  }
  get colorSchemeClass(): string {
    return this.colorScheme + '-color'
  }
  get classNames(): string[] {
    return [
      'button',
      !(this.hasIcon && !this.hasText) ? this.variationClass : '',
      this.colorSchemeClass,
      this.iconClass,
      this.xTextMargin ? '' : 'no-text-margin',
    ]
  }

  click(event): void {
    if (this.href) {
      navigateTo(this.href, { target: this.hrefTarget })
    }
    this.$emit('click', event)
  }
}
